import DoxAnalytics from '@dox/analytics';

/*
To use the Dox Analytics Markup for tracking user interaction via HTML DOM markup, follow these steps:

1. **Section**: Wrap the section you want to track setting `data-da-section="SECTION-NAME-HERE"`.
   Elements with events must be inside a section, otherwise will not be tracked.
   For example, `<div data-da-section="login-form">`.

2. **Event**: Set the event in the field itself with `data-da-event="ACTION-NAME-HERE"`.
   For example, `<button data-da-event="auth_login_button_signin">Sign In</button>`.

3 . **Properties**: Properties are optional, and are attached to the event.
    Properties might be static such as `data-da-event-properties='{ "input": "something' }'`.
    Or dynamically loaded from a element's value `data-da-event-properties='{ "email_address": "value:[data-sel-email]" }'`.
*/
export default class DoxAnalyticsMarkup {
  constructor(document) {
    this.document = document;

    // General configuration
    this.application = this.__getMeta("application");
    this.release = this.__getMeta("application-release");
    this.directReferrerDomain = this.__getMeta("direct-referrer-domain");
    this.warnInvalid = true;
    this.product = this.__getMeta("product");
    this.view = this.__getMeta("view");
    this.userId = this.__getMeta("user-id");

    this.hasInitted = false;
    this.shouldLog = false;

    this.errorHandler = () => {};
    if (this.__getMeta("errors") === "true") {
      this.errorHandler = (error, errorData) => {
        this.log("dox-analytics error:");
        throw error;
      };
    }

    this.errorHandler = this.errorHandler.bind(this);
    this.setup = this.setup.bind(this);
    this.initSectionTracking = this.initSectionTracking.bind(this);
    this.__setupSectionElement = this.__setupSectionElement.bind(this);
    this.__setupImpressionElement = this.__setupImpressionElement.bind(this);
    this.__eventPropertiesForElement = this.__eventPropertiesForElement.bind(this);

    this.setup();
  }

  setup() {
    this.da = DoxAnalytics({
      application: this.application,
      release: this.release,
      directReferrerDomain: this.directReferrerDomain,
      warnInvalid: this.warnInvalid,
      errorHandler: this.errorHandler,
    });

    if (this.userId) this.da.identify({ userId: this.userId });
    this.da.page({ product: this.product, view: this.view });

    this.document.addEventListener("DOMContentLoaded", () => {
      this.initSectionTracking()
    })

    addEventListener("beforeunload",  (event) => {
      this.log('[da] beforeunload')
    });
  }

  initSectionTracking() {
    if (this.hasInitted === true) { return }
    this.hasInitted = true;

    this.document
      .querySelectorAll("[data-da-section]")
      .forEach((sectionElement) => this.__setupSectionElement(sectionElement));
  }

  __setupSectionElement(sectionElement) {
    const section = this.da.registerSection({ product: this.product });

    sectionElement
      .querySelectorAll("[data-da-event]")
      .forEach((impressionElement) =>
        this.__setupImpressionElement(impressionElement, section)
      );

    return section;
  }

  log(type, event) {
    if (this.shouldLog) {
      console.log('[da]', type, event)
    }
  }

  __setupImpressionElement(impressionElement, section) {
    const impression = section.registerImpression();

    const eventName = impressionElement.getAttribute("data-da-event");
    const trackName =
      impressionElement.getAttribute("data-da-track") ||
      this.__autodetectTrackAction(impressionElement);

    if (trackName === "tapped") {
      impressionElement.addEventListener("click", (event) => {
        this.log('tapped', event)
      });
    }

    let id;
    if (trackName === "entered") {
      id = impression.trackEntered(eventName, impressionElement, () =>
        this.__eventPropertiesForElement(impressionElement)
      );
    } else if (trackName === "tapped") {
      id = impression.trackTapped(eventName, impressionElement, () =>
        this.__eventPropertiesForElement(impressionElement)
      );
    } else {
      this.log(
        `da: unknown track action ${trackName} for event ${eventName}`
      );
    }

    return [impression, eventName, trackName, id];
  }

  // Returns the event properties for the given element, if any.
  //
  // Properties might be static such as `data-da-event-properties='{ "input": "something' }'`.
  // Or dynamically loaded from a element's value `data-da-event-properties='{ "email_address": "value:[data-sel-email]" }'`.
  __eventPropertiesForElement(impressionElement) {
    const rawProperties = impressionElement.getAttribute(
      "data-da-event-properties"
    );
    if (!rawProperties) return {};

    let parsedProperties;
    try {
      parsedProperties = JSON.parse(rawProperties);
    } catch (error) {
      this.log("da: invalid event properties", rawProperties);
    }

    return Object.fromEntries(
      Object.entries(parsedProperties).map(([key, value]) => {
        if (value.startsWith("value:")) {
          const selector = value.replace("value:", "");
          value = document.querySelector(selector).value;
        }
        return [key, value];
      })
    );
  }

  __getMeta(key) {
    return this.document.querySelector(`meta[name=dox-analytics-${key}]`)
      ?.content;
  }

  __autodetectTrackAction(element) {
    const tagName = element.tagName.toLowerCase();

    if (["input"].includes(tagName)) {
      return "entered";
    } else if (["button", "a"].includes(tagName)) {
      return "tapped";
    } else {
      // default
      return "tapped";
    }
  }
}
